import { useIntl } from 'react-intl';
import { useCallback } from 'react';
import { useLocation } from 'react-router';

import { ErrorNotification } from 'app/components/error-notification/error-notification';
import { Modal } from 'app/components/ui/modal';
import { ROUTES } from 'app/shared/consts/paths';
import { getErrorDetails } from 'app/shared/utils/get-error-details';
import { ErrorType } from 'app/shared/consts/error-translations';
import { useErrors } from 'app/shared/contexts/errors';

/**
 * Popup for the recoverable `errors` channel. A terminal error takes
 * precedence: one failure (e.g. an expired token) often fails a terminal query
 * and a recoverable one at once, and the user should see a single error, the
 * `/error` page, not the page with this modal on top. `genericErrors` is
 * checked as well as the route so the modal also stays shut for the render
 * before `ErrorNavigator` has navigated.
 */
export const ErrorModal = (): JSX.Element | null => {
  const { formatMessage, messages } = useIntl();
  const { errors, genericErrors, hideErrors } = useErrors();
  const { pathname } = useLocation();
  const isTerminalErrorShown = genericErrors.length > 0 || pathname === ROUTES.ERROR;
  const handleClose = useCallback(() => {
    hideErrors();
  }, [hideErrors]);

  const { titleTranslationId, descriptionTranslationId, buttonTextTranslationId } = getErrorDetails(
    errors,
    messages,
    ErrorType.ERRORS,
  );

  const title = formatMessage({ id: titleTranslationId });
  const description = formatMessage({ id: descriptionTranslationId });

  return (
    <Modal isOpen={errors.length > 0 && !isTerminalErrorShown} title={title} description={description} onClose={handleClose}>
      <ErrorNotification
        title={title}
        description={description}
        buttonText={formatMessage({ id: buttonTextTranslationId })}
        onClick={handleClose}
      />
    </Modal>
  );
};
